import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { MapPin, ArrowUpRight } from "lucide-react";
import { IMAGES } from "../lib/api";
import { Reveal, MaskedLine } from "./motion/Reveal";

const primaryCities = [
  "Amsterdam", "Amstelveen", "Haarlem", "Zaandam",
  "Purmerend", "Almere", "Hoofddorp", "Hilversum",
];
const secondaryAreas = [
  "Aalsmeer", "Beverwijk", "Diemen", "Edam-Volendam", "Heemstede",
  "IJmuiden", "Landsmeer", "Ouder-Amstel", "Uithoorn", "Weesp", "Zandvoort",
];

export default function ServiceArea() {
  const { t } = useTranslation();
  return (
    <section id="werkgebied" data-testid="service-area-section" className="section bg-[#0A1128] relative overflow-hidden">
      <div
        aria-hidden
        className="absolute inset-0 opacity-10"
        style={{ backgroundImage: `url(${IMAGES.amsterdam})`, backgroundSize: "cover", backgroundPosition: "center" }}
      />
      <div className="absolute inset-0 bg-gradient-to-r from-[#0A1128] via-[#0A1128]/95 to-[#0A1128]/70" />

      <div className="relative max-w-7xl mx-auto px-5 md:px-10 grid lg:grid-cols-12 gap-10">
        <div className="lg:col-span-5">
          <Reveal>
            <div className="flex items-center gap-3 mb-4">
              <span className="accent-bar" />
              <span className="text-[#FF5A1F] uppercase font-bold tracking-widest text-xs">
                {t("area.eyebrow")}
              </span>
            </div>
          </Reveal>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl text-white mb-5 leading-[1.02]">
            <span className="block overflow-hidden"><MaskedLine text={t("area.title_1")} wordDelay={0.06} /></span>
            <span className="block overflow-hidden text-[#FF5A1F]"><MaskedLine text={t("area.title_2")} wordDelay={0.06} delay={0.15} /></span>
          </h2>
          <Reveal delay={0.15}>
            <p className="text-base md:text-lg text-white/70 leading-relaxed mb-6 max-w-lg">
              {t("area.subtitle")}
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <a href="#offerte" className="inline-flex items-center gap-2 text-[#FF5A1F] uppercase font-bold tracking-wider text-sm hover:gap-3 transition-all">
              {t("area.cta")} <ArrowUpRight className="w-4 h-4" />
            </a>
          </Reveal>
        </div>

        <div className="lg:col-span-7">
          <motion.div
            initial="hidden"
            whileInView="show"
            viewport={{ once: true, margin: "-60px 0px" }}
            variants={{ hidden: {}, show: { transition: { staggerChildren: 0.05 } } }}
            className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6"
          >
            {primaryCities.map((city, i) => (
              <motion.div
                key={city}
                data-testid={`area-city-${i}`}
                variants={{ hidden: { opacity: 0, y: 20 }, show: { opacity: 1, y: 0, transition: { duration: 0.6 } } }}
                whileHover={{ y: -3 }}
                className="group bg-white/5 border border-white/10 hover:border-[#FF5A1F] hover:bg-white/[0.08] transition-colors p-4 md:p-5 backdrop-blur-sm cursor-default"
              >
                <MapPin className="w-4 h-4 text-[#FF5A1F] mb-2 group-hover:scale-110 transition-transform" />
                <div className="font-display text-white text-base md:text-lg leading-tight">{city}</div>
              </motion.div>
            ))}
          </motion.div>

          <div className="border-t border-white/10 pt-6">
            <div className="text-xs uppercase tracking-widest text-white/50 font-bold mb-3">
              {t("area.also_active")}
            </div>
            <div className="flex flex-wrap gap-2">
              {secondaryAreas.map((area) => (
                <span
                  key={area}
                  className="text-xs md:text-sm text-white/80 bg-white/[0.03] border border-white/10 px-3 py-1.5 hover:border-white/25 transition-colors"
                >
                  {area}
                </span>
              ))}
              <span className="text-xs md:text-sm text-[#FF5A1F] bg-[#FF5A1F]/10 border border-[#FF5A1F]/30 px-3 py-1.5 font-bold uppercase tracking-wide">
                {t("area.rest_of_nh")}
              </span>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
